import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// PrimeNG imports
import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';

// Models and services
import { 
  ProductSkuResponse, 
  ProductResponse, 
  ListProductSkusResponse
} from '../../models';
import { ProductSkuService } from '../../services/product-sku.service';
import { CommonService, ToastMessageService } from '@fitness-force/api';
import { ConfirmationService } from 'primeng/api';

// Import sub-components
import { SkuFormComponent } from '../sku-form/sku-form.component';
import { SelectModule } from 'primeng/select';
import { CurrencyformatPipe } from '@fitness-force/pipes/currency';

@Component({
  selector: 'lib-sku-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    DialogModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    ToastModule,
    ConfirmDialogModule,
    TagModule,
    SelectModule,
    IconFieldModule,
    InputIconModule,
    SkuFormComponent,
    CurrencyformatPipe
  ],
  providers: [ConfirmationService],
  changeDetection: ChangeDetectionStrategy.Default,
  template: `
    <p-dialog 
      [(visible)]="visible"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '1200px', height: '90vh' }"
      (onHide)="onClose.emit()">
      
      <ng-template pTemplate="header">
        <div class="flex align-items-center gap-3">
          <div class="flex align-items-center gap-3">
            <img 
              *ngIf="product?.image_url" 
              [src]="product!.image_url" 
              [alt]="product!.name_primary_language"
              class="w-3rem h-3rem border-round object-cover" />
            <div>
              <h3 class="m-0 text-lg font-semibold">{{ 'RETAIL_BILLING.SKU.MANAGE' | translate }}</h3>
              <div class="text-sm text-500 mt-1">
                <span class="font-medium">{{ product?.name_primary_language }}</span>
                <span *ngIf="product?.category?.name_primary_language" class="ml-2">• {{ product?.category?.name_primary_language }}</span>
              </div>
            </div>
          </div>
        </div>
      </ng-template>

      <div class="card">
        <div class="flex justify-content-between align-items-center mb-4">
          <div class="flex gap-3 align-items-center">
            <p-iconField iconPosition="left">
              <p-inputIcon styleClass="pi pi-search"></p-inputIcon>
              <input 
                pInputText 
                type="text" 
                [placeholder]="'common.PLACEHOLDER.SEARCH' | translate"
                [(ngModel)]="searchTerm"
                (ngModelChange)="onSearch()"
                aria-label="search-skus-input" />
            </p-iconField>
            
            <p-select
              [options]="statusFilterOptions"
              [(ngModel)]="selectedStatusFilter"
              [placeholder]="'RETAIL_BILLING.SKU.FILTER_BY_STATUS' | translate"
              optionLabel="label"
              optionValue="value"
              [showClear]="true"
              (onChange)="onStatusFilterChange()"
              [style]="{ minWidth: '200px' }"
              data-testid="status-filter-dropdown">
            </p-select>
          </div>
          
          <p-button
            [label]="'RETAIL_BILLING.SKU.CREATE' | translate"
            icon="pi pi-plus"
            (onClick)="showCreateDialog()"
            aria-label="create-sku-button">
          </p-button>
        </div>

        <!-- SKUs table -->
        <p-table 
          [value]="skus" 
          [loading]="loading"
          [paginator]="false"
          [rows]="pageSize"
          [totalRecords]="totalRecords"
          [lazy]="true"
          (onLazyLoad)="loadSkus($event)"
          [showCurrentPageReport]="false"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          [rowsPerPageOptions]="[10, 25, 50]"
          styleClass="p-datatable-gridlines">
          
          <ng-template pTemplate="header">
            <tr>
              <th>{{ 'RETAIL_BILLING.SKU.SKU_NAME' | translate }}</th>
              <th>{{ 'RETAIL_BILLING.SKU.SKU_CODE' | translate }}</th>
              <th>{{ 'RETAIL_BILLING.SKU.BASE_PRICE' | translate }}</th>
              <th>{{ 'RETAIL_BILLING.SKU.ATTRIBUTES' | translate }}</th>
              <th>{{ 'RETAIL_BILLING.PRODUCTS.STATUS' | translate }}</th>
              <th>{{ 'RETAIL_BILLING.COMMON.ACTIONS' | translate }}</th>
            </tr>
          </ng-template>
          
          <ng-template pTemplate="body" let-sku>
            <tr>
              <td>
                <div class="flex align-items-center">
                  <img 
                    *ngIf="sku.image_url" 
                    [src]="sku.image_url" 
                    [alt]="sku.variant_name_primary_language"
                    class="w-3rem h-3rem mr-2 border-round" />
                  <div>
                    <div class="font-medium">{{ sku.variant_name_primary_language }}</div>
                    <div class="text-sm text-500">{{ sku.variant_name_secondary_language }}</div>
                  </div>
                </div>
              </td>
              <td>
                <span class="font-mono text-sm">{{ sku.sku_code }}</span>
              </td>
              <td>
                <span class="font-semibold">{{ sku.unit_price_minor | currencyformat }}</span>
              </td>
              <td>
                <div  class="flex gap-1 flex-wrap">
                  {{ sku.attributes_json.length || 0 }}
                </div>
              </td>
              <td>
                <p-tag 
                  [value]="sku.is_active ? ('RETAIL_BILLING.PRODUCTS.ACTIVE' | translate) : ('RETAIL_BILLING.PRODUCTS.INACTIVE' | translate)"
                  [severity]="sku.is_active ? 'success' : 'danger'">
                </p-tag>
              </td>
              <td>
                <div class="flex gap-2">
                  <p-button
                    icon="pi pi-pencil"
                    tooltipPosition="top"
                    tooltip="Edit SKU"
                    [text]="true"
                    [rounded]="true"
                    [outlined]="true"
                    size="small"
                    (onClick)="editSku(sku)"
                    [ariaLabel]="'edit-sku-' + sku.id">
                  </p-button>
                  <p-button *ngIf="false" 
                    icon="pi pi-trash"
                    tooltipPosition="top"
                    tooltip="Delete SKU"
                    [text]="true"
                    [rounded]="true"
                    [outlined]="true"
                    size="small"
                    severity="danger"
                    (onClick)="deleteSku(sku)"
                    [ariaLabel]="'delete-sku-' + sku.id">
                  </p-button>
                </div>
              </td>
            </tr>
          </ng-template>
          
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="6" class="text-center">{{ 'RETAIL_BILLING.SKU.NO_SKUS' | translate }}</td>
            </tr>
          </ng-template>
        </p-table>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex justify-content-end">
          <p-button 
            [label]="'RETAIL_BILLING.COMMON.CLOSE' | translate"
            icon="pi pi-times"
            [text]="true"
            (onClick)="onClose.emit()"
            aria-label="close-button">
          </p-button>
        </div>
      </ng-template>
    </p-dialog>

    <!-- Create/Edit SKU Dialog -->
    <lib-sku-form
      *ngIf="showSkuDialog"
      [(visible)]="showSkuDialog"
      [sku]="selectedSku"
      [product]="product"
      [tenantId]="tenantId"
      [companyLevel]="companyLevel"
      [primaryLanguage]="primaryLanguage"
      [secondaryLanguage]="secondaryLanguage"
      [isSecondaryLanguage]="isSecondaryLanguage"
      (onSave)="onSkuSaved($event)"
      (onCancel)="hideSkuDialog()">
    </lib-sku-form>

    <p-confirmDialog></p-confirmDialog>
    <p-toast></p-toast>
  `,
})
export class SkuListComponent implements OnInit {
  @Input() visible = false;
  @Input() product: ProductResponse | null = null;
  @Input() tenantId?: number;
  @Input() companyLevel: boolean = true;
  @Input() primaryLanguage: string = 'English';
  @Input() secondaryLanguage: string = 'Arabic';
  @Input() isSecondaryLanguage: boolean = false;
  
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onClose = new EventEmitter<void>();

  skus: ProductSkuResponse[] = [];
  filteredSkus: ProductSkuResponse[] = [];
  loading = false;
  totalRecords = 0;
  pageSize = 10;
  
  searchTerm = '';
  selectedStatusFilter: boolean | null = null;
  statusFilterOptions = [
    { label: '', value: true },
    { label: '', value: false }
  ];
  
  showSkuDialog = false;
  selectedSku: ProductSkuResponse | null = null;
  requestParams = new Map();

  
  constructor(
    private skuService: ProductSkuService,
    private toastService: ToastMessageService,
    private confirmationService: ConfirmationService,
    private translate: TranslateService,
    private changeDetectorRef: ChangeDetectorRef,
    private commonService: CommonService
  ) {}

  ngOnInit() {
      this.requestParams.set('PageNumber', { name: 'PageNumber', value: 1 });
      this.requestParams.set('PageSize', { name: 'PageSize', value: 10 });
      this.initializeStatusFilterOptions();
  }

  initializeStatusFilterOptions() {
    this.statusFilterOptions = [
      { 
        label: this.translate.instant('RETAIL_BILLING.PRODUCTS.ACTIVE'), 
        value: true 
      },
      { 
        label: this.translate.instant('RETAIL_BILLING.PRODUCTS.INACTIVE'), 
        value: false 
      }
    ];
  }


  loadSkus(event?: any) {
    if (!this.product) return;
    
    this.loading = true;

    // Clear previous search/filter params
    this.requestParams.delete('search_term');
    this.requestParams.delete('is_active');

    // Add search term if exists
    if(this.searchTerm) {
      this.requestParams.set('search_term', { name: 'search_term', value: this.searchTerm });
    }

    // Add status filter if selected
    if(this.selectedStatusFilter !== null) {
      this.requestParams.set('is_active', { name: 'is_active', value: this.selectedStatusFilter });
    }

    const loadFn = this.companyLevel
      ? this.skuService.getCompanyProductSkus(this.product.id, this.requestParams)
      : this.skuService.getTenantProductSkus(this.tenantId!, this.product.id, this.requestParams);

    loadFn.subscribe({
      next: (response:ListProductSkusResponse) => {
        this.skus = response.items || [];
        this.totalRecords = response.total_items;
        this.loading = false;
        this.changeDetectorRef.detectChanges();
      },
      error: (error) => {
        this.toastService.showError(error);
        this.loading = false;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  onSearch() {
    this.loadSkus();
  }

  onStatusFilterChange() {
    this.loadSkus();
  }

  onFilter() {
    this.loadSkus();
  }

  showCreateDialog() {
    this.selectedSku = null;
    this.showSkuDialog = true;
  }

  editSku(sku: ProductSkuResponse) {
    this.selectedSku = sku;
    this.showSkuDialog = true;
  }

  deleteSku(sku: ProductSkuResponse) {
    this.confirmationService.confirm({
      message: this.translate.instant('RETAIL_BILLING.COMMON.CONFIRM_DELETE'),
      header: this.translate.instant('RETAIL_BILLING.SKU.DELETE'),
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        // TODO: Implement delete functionality
        this.toastService.showSuccess(this.translate.instant('RETAIL_BILLING.SKU.DELETE_SUCCESS'));
        this.loadSkus();
      }
    });
  }

  onSkuSaved(sku: ProductSkuResponse) {
    this.hideSkuDialog();
    this.loadSkus();
    const message = this.selectedSku 
      ? 'RETAIL_BILLING.SKU.UPDATE_SUCCESS'
      : 'RETAIL_BILLING.SKU.CREATE_SUCCESS';
    this.toastService.showSuccess(this.translate.instant(message));
  }

  hideSkuDialog() {
    this.showSkuDialog = false;
    this.selectedSku = null;
  }

  // Removed getBillingFrequencyLabel and getSubscriptionTypeLabel as they are no longer used
}